/**
 * NeoCorePlazosFijosPage — listado y creación de Plazos Fijos.
 *
 * Caso de uso primario: el asociado deposita por caja/transferencia y pide
 * asistencia al operador para abrir el plazo fijo. El operador lo crea acá
 * directamente como activo (sin pasar por solicitud desde la app).
 *
 * Listado: muestra todos los PFs con detalle (los legacy sin detalle no
 * aparecen acá hasta que hagamos la migración Fase 2.5). Filtros básicos
 * por estado.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack as Layers,
  Plus,
  MagnifyingGlass as Search,
  CheckCircle,
  Coins,
  Hourglass,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { CrearPlazoFijoModal } from '@/components/CrearPlazoFijoModal'

type PlazoFijoRow = {
  id: string
  cuenta_id: string
  asociado_id: string
  capital_inicial: number
  tasa_anual: number
  plazo_dias: number
  manejo_capitalizacion: string
  estado_pf: string
  fecha_apertura: string
  fecha_vencimiento: string
  certificado_no: string | null
  asociado_nombre?: string
  asociado_no?: string
}

const ESTADO_LABEL: Record<string, { label: string; color: string }> = {
  solicitado: { label: 'Solicitado', color: '#6B6B6B' },
  pendiente_de_caja: { label: 'Pendiente ingreso', color: '#B8860B' },
  activo: { label: 'Activo', color: '#0F8A57' },
  vencido: { label: 'Vencido', color: '#D4A017' },
  renovado: { label: 'Renovado', color: '#0F8A57' },
  liquidado: { label: 'Liquidado', color: '#888780' },
  cancelado_anticipado: { label: 'Cancelado', color: '#A32D2D' },
  rechazado: { label: 'Rechazado', color: '#A32D2D' },
}

const PLAZO_LABEL: Record<number, string> = {
  180: '6 meses',
  365: '1 año',
  545: '1.5 años',
  730: '2 años',
}

function formatQ(n: number): string {
  return `Q ${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function formatFecha(iso: string): string {
  // Columna DATE ('YYYY-MM-DD'): anclar mediodía para no correrse un día en GT (UTC-6)
  return new Date(iso.length === 10 ? iso + 'T12:00:00' : iso).toLocaleDateString('es-GT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function NeoCorePlazosFijosPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<PlazoFijoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [crearOpen, setCrearOpen] = useState(false)

  const cargar = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Join manual: PFs + asociado para mostrar nombre. Por ahora simple,
      // si crece la tabla lo movemos a una vista o RPC.
      const { data: pfs, error: pfErr } = await supabase
        .from('plazo_fijo_detalle')
        .select(`
          id, cuenta_id, asociado_id, capital_inicial, tasa_anual, plazo_dias,
          manejo_capitalizacion, estado_pf, fecha_apertura, fecha_vencimiento,
          certificado_no
        `)
        .order('fecha_apertura', { ascending: false })

      if (pfErr) throw pfErr
      const list = (pfs ?? []) as PlazoFijoRow[]

      // Bulk fetch de asociados para evitar N+1
      if (list.length > 0) {
        const ids = Array.from(new Set(list.map((r) => r.asociado_id)))
        const { data: asociados } = await supabase
          .from('asociado')
          .select('id, nombre_completo, no_asociado')
          .in('id', ids)
        const byId = new Map(
          (asociados ?? []).map((a: any) => [a.id, a]),
        )
        for (const r of list) {
          const a = byId.get(r.asociado_id)
          if (a) {
            r.asociado_nombre = a.nombre_completo
            r.asociado_no = a.no_asociado
          }
        }
      }

      setRows(list)
    } catch (e: any) {
      setError(e?.message ?? 'No pudimos cargar los plazos fijos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    cargar()
  }, [cargar])

  const rowsFiltradas = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      return (
        r.cuenta_id.toLowerCase().includes(q) ||
        (r.certificado_no ?? '').toLowerCase().includes(q) ||
        (r.asociado_nombre ?? '').toLowerCase().includes(q) ||
        (r.asociado_no ?? '').toLowerCase().includes(q)
      )
    })
  }, [rows, search])

  const stats = useMemo(() => {
    const activos = rows.filter((r) => r.estado_pf === 'activo')
    const totalCapital = activos.reduce((s, r) => s + Number(r.capital_inicial), 0)
    const proximoVence = activos
      .map((r) =>
        new Date(
          r.fecha_vencimiento.length === 10 ? r.fecha_vencimiento + 'T12:00:00' : r.fecha_vencimiento,
        ).getTime(),
      )
      .sort((a, b) => a - b)[0]
    return {
      total: rows.length,
      activos: activos.length,
      totalCapital,
      proximoVence: proximoVence ? new Date(proximoVence) : null,
    }
  }, [rows])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Header con stats inline + acción */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div className="stats-inline">
          <span className="stat-num">{stats.activos}</span>
          <span className="stat-label">activos</span>
          {stats.totalCapital > 0 ? (
            <>
              <span className="stat-sep">·</span>
              <span className="stat-num">{formatQ(stats.totalCapital)}</span>
              <span className="stat-label">en cartera pasiva</span>
            </>
          ) : null}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setCrearOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={14} weight="bold" />
          Nuevo plazo fijo
        </button>
      </div>

      <CrearPlazoFijoModal
        open={crearOpen}
        onClose={() => setCrearOpen(false)}
        onCreated={(resultado) => {
          setCrearOpen(false)
          cargar()
          if (resultado?.plazo_fijo_id) {
            navigate(`/neocore/plazos-fijos/${resultado.plazo_fijo_id}`)
          }
        }}
      />

      {/* KPIs en metric cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <KpiTile
          Icon={Layers}
          label="Total PFs"
          value={String(stats.total)}
          tone="neutral"
        />
        <KpiTile
          Icon={CheckCircle}
          label="Activos"
          value={String(stats.activos)}
          tone="positive"
        />
        <KpiTile
          Icon={Coins}
          label="Cartera pasiva"
          value={formatQ(stats.totalCapital)}
          tone="brand"
        />
        <KpiTile
          Icon={Hourglass}
          label="Próximo vencimiento"
          value={
            stats.proximoVence
              ? stats.proximoVence.toLocaleDateString('es-GT', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : '—'
          }
          tone="warn"
        />
      </div>

      {/* Search */}
      <div className="search-input-wrap" style={{ maxWidth: 480 }}>
        <Search size={14} weight="bold" className="search-icon" />
        <input
          placeholder="Buscar por cuenta, certificado, nombre o no. asociado…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tabla */}
      <div className="card-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>Asociado</th>
              <th>Cuenta</th>
              <th>Certificado</th>
              <th style={{ textAlign: 'right' }}>Capital</th>
              <th>Plazo</th>
              <th>Tasa</th>
              <th>Vence</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>
                  Cargando…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: 'center', padding: 24, color: '#A32D2D' }}
                >
                  {error}
                </td>
              </tr>
            ) : rowsFiltradas.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--n-500)' }}>
                  {search
                    ? 'No hay resultados con ese filtro.'
                    : 'Todavía no hay plazos fijos creados desde el nuevo core. Usá "Nuevo plazo fijo" arriba para empezar.'}
                </td>
              </tr>
            ) : (
              rowsFiltradas.map((r) => {
                const estadoMeta = ESTADO_LABEL[r.estado_pf] ?? {
                  label: r.estado_pf,
                  color: '#6B6B6B',
                }
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/neocore/plazos-fijos/${r.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {r.asociado_nombre ?? '—'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 2 }}>
                        {r.asociado_no}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                      {r.cuenta_id}
                    </td>
                    <td style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                      {r.certificado_no ?? '—'}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 500,
                      }}
                    >
                      {formatQ(Number(r.capital_inicial))}
                    </td>
                    <td>
                      {PLAZO_LABEL[r.plazo_dias] ?? `${r.plazo_dias} días`}
                      <div style={{ fontSize: 10, color: 'var(--n-500)' }}>
                        {r.manejo_capitalizacion === 'MENSUAL' ? 'Mensual' : 'Al vencer'}
                      </div>
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {(Number(r.tasa_anual) * 100).toFixed(2)}%
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {formatFecha(r.fecha_vencimiento)}
                    </td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          fontSize: 11,
                          fontWeight: 600,
                          color: estadoMeta.color,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: `${estadoMeta.color}1A`,
                        }}
                      >
                        {estadoMeta.label}
                      </span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

type IconType = typeof Layers

function KpiTile({
  Icon,
  label,
  value,
  tone,
}: {
  Icon: IconType
  label: string
  value: string
  tone: 'neutral' | 'positive' | 'brand' | 'warn'
}) {
  const colors: Record<string, { bg: string; fg: string }> = {
    neutral: { bg: 'var(--n-100)', fg: 'var(--n-700)' },
    positive: { bg: 'rgba(15,138,87,0.10)', fg: '#0F8A57' },
    brand: { bg: 'rgba(240,78,35,0.10)', fg: '#F04E23' },
    warn: { bg: 'rgba(212,160,23,0.10)', fg: '#B8860B' },
  }
  const c = colors[tone]
  return (
    <div className="kpi-card" style={{ position: 'relative' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 8,
          background: c.bg,
          color: c.fg,
          marginBottom: 8,
        }}
      >
        <Icon size={14} weight="duotone" />
      </div>
      <div className="label">{label}</div>
      <div className="value" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  )
}
